import React from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import { MediaKind } from "@perceptron/utils/types";

import { MediaFactory } from "@demo/libs/media-factory";
import { MediaData } from "@demo/types/base";
import { getImageSrc } from "@demo/utils/media";

import { PlayGlyph } from "./PlayGlyph";
import { useVideoPoster } from "./useVideoPoster";

interface MediaThumbProps {
  media: MediaData;
  /** Zero-based position, used for the accessible label. */
  index: number;
  size: number;
  active: boolean;
  onSelect: () => void;
}

/**
 * One filmstrip thumbnail: image or video poster with a centred play glyph.
 * The active state is a white ring so it reads on any photo; the rest sit at
 * 60% until hovered.
 */
export function MediaThumb({ media, index, size, active, onSelect }: MediaThumbProps) {
  const isVideo = MediaFactory.detectMediaKind(media) === MediaKind.Video;
  // Videos show a captured still rather than a live element — see useVideoPoster.
  const poster = useVideoPoster(isVideo ? media.url : undefined);
  const src = isVideo ? poster : getImageSrc(media);

  return (
    <button
      aria-current={active}
      aria-label={formatString(strings.mediaCarousel.showMedia, index + 1) as string}
      className="relative shrink-0 cursor-pointer"
      style={{ width: size, height: size }}
      type="button"
      onClick={onSelect}
    >
      <div
        className={cn(
          "absolute inset-0 overflow-hidden rounded-lg border border-solid transition-[opacity,border-color,box-shadow] duration-200",
          active
            ? "border-white opacity-100 shadow-[0px_0px_0px_1px_rgba(255,255,255,0.35)]"
            : "border-[rgba(255,255,255,0.15)] opacity-60 hover:opacity-100",
        )}
      >
        {src ? (
          // The rail renders many small crops; next/image adds no value at 56px
          // and would need a per-source remote pattern for S3/CDN URLs.
          // eslint-disable-next-line @next/next/no-img-element
          <img alt="" className="size-full object-cover" src={src} />
        ) : (
          // A video whose poster has not been captured yet (or could not be).
          <div className="size-full bg-background-alt-2" />
        )}
        {isVideo && <PlayGlyph size={14} />}
      </div>
    </button>
  );
}
